import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import { FilterState, SavedFilter } from '../types';

interface FilterSidebarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  savedFilters: SavedFilter[];
  onSaveFilter: (name: string) => void;
  onLoadFilter: (filter: SavedFilter) => void;
  onDeleteFilter: (id: string) => void;
  onResetFilters: () => void;
}

const FilterSidebar = ({
  filters,
  onFilterChange,
  savedFilters,
  onSaveFilter,
  onLoadFilter,
  onDeleteFilter,
  onResetFilters
}: FilterSidebarProps) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [expandedSections, setExpandedSections] = useState<string[]>(['date', 'status']);

  const statusOptions = [
    { value: 'success', label: 'Sucesso' },
    { value: 'api_error', label: 'Erro de API' },
    { value: 'validation_error', label: 'Erro de Validação' },
    { value: 'pending', label: 'Pendente' },
    { value: 'processing', label: 'Processando' }
  ];

  const ufOptions = [
    { value: 'SP', label: 'São Paulo' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'PR', label: 'Paraná' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'BA', label: 'Bahia' },
    { value: 'PE', label: 'Pernambuco' }
  ];

  const taxRegimeOptions = [
    { value: 'simples_nacional', label: 'Simples Nacional' },
    { value: 'lucro_presumido', label: 'Lucro Presumido' },
    { value: 'lucro_real', label: 'Lucro Real' }
  ];

  const freshnessOptions = [
    { value: 'current', label: 'Atualizado' },
    { value: 'outdated', label: 'Desatualizado' },
    { value: 'requires_update', label: 'Requer Atualização' }
  ];

  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const handleSaveFilter = () => {
    if (filterName.trim()) {
      onSaveFilter(filterName.trim());
      setFilterName('');
      setShowSaveDialog(false);
    }
  };

  const handleStatusChange = (value: string, checked: boolean) => {
    const newStatus = checked
      ? [...filters.status, value]
      : filters.status.filter(s => s !== value);
    onFilterChange({ ...filters, status: newStatus });
  };

  const handleUfOriginChange = (value: string, checked: boolean) => {
    const newUfOrigin = checked
      ? [...filters.ufOrigin, value]
      : filters.ufOrigin.filter(uf => uf !== value);
    onFilterChange({ ...filters, ufOrigin: newUfOrigin });
  };

  const handleUfDestinationChange = (value: string, checked: boolean) => {
    const newUfDestination = checked
      ? [...filters.ufDestination, value]
      : filters.ufDestination.filter(uf => uf !== value);
    onFilterChange({ ...filters, ufDestination: newUfDestination });
  };

  const handleTaxRegimeChange = (value: string, checked: boolean) => {
    const newTaxRegime = checked
      ? [...filters.taxRegime, value]
      : filters.taxRegime.filter(tr => tr !== value);
    onFilterChange({ ...filters, taxRegime: newTaxRegime });
  };

  const handleFreshnessChange = (value: string, checked: boolean) => {
    const newFreshness = checked
      ? [...filters.apiDataFreshness, value]
      : filters.apiDataFreshness.filter(f => f !== value);
    onFilterChange({ ...filters, apiDataFreshness: newFreshness });
  };

  return (
    <aside className="w-[300px] bg-card border-r border-border h-full overflow-y-auto">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Filtros</h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="RotateCcw"
            onClick={onResetFilters}
          >
            Limpar
          </Button>
        </div>

        <Input
          type="search"
          placeholder="Buscar empresa..."
          value={filters.companyName}
          onChange={(e) => onFilterChange({ ...filters, companyName: e.target.value })}
          className="mb-3"
        />

        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="Save"
            iconPosition="left"
            onClick={() => setShowSaveDialog(true)}
          >
            Salvar Filtro
          </Button>
        </div>
      </div>

      {showSaveDialog && (
        <div className="p-4 bg-muted border-b border-border">
          <Input
            label="Nome do Filtro"
            type="text"
            placeholder="Ex: Consultas SP 2024"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            className="mb-3"
          />
          <div className="flex gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleSaveFilter}
              disabled={!filterName.trim()}
            >
              Salvar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowSaveDialog(false);
                setFilterName('');
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {savedFilters.length > 0 && (
        <div className="p-4 border-b border-border">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Filtros Salvos</h3>
          <div className="space-y-2">
            {savedFilters.map((filter) => (
              <div
                key={filter.id}
                className="flex items-center justify-between p-2 bg-muted rounded-md hover:bg-muted/80 transition-colors"
              >
                <button
                  onClick={() => onLoadFilter(filter)}
                  className="flex-1 text-left text-sm text-foreground hover:text-primary"
                >
                  {filter.name}
                </button>
                <button
                  onClick={() => onDeleteFilter(filter.id)}
                  className="p-1 hover:bg-destructive/10 rounded"
                  aria-label="Excluir filtro"
                >
                  <Icon name="Trash2" size={14} color="var(--color-destructive)" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        <div>
          <button
            onClick={() => toggleSection('date')}
            className="flex items-center justify-between w-full mb-3"
          >
            <span className="text-sm font-medium text-foreground">Período</span>
            <Icon
              name={expandedSections.includes('date') ? 'ChevronUp' : 'ChevronDown'}
              size={16}
              color="var(--color-muted-foreground)"
            />
          </button>
          {expandedSections.includes('date') && (
            <div className="space-y-3">
              <Input
                label="Data Inicial"
                type="date"
                value={filters.dateRange.start ? filters.dateRange.start.toISOString().split('T')[0] : ''}
                onChange={(e) => onFilterChange({
                  ...filters,
                  dateRange: { ...filters.dateRange, start: e.target.value ? new Date(e.target.value) : null }
                })}
              />
              <Input
                label="Data Final"
                type="date"
                value={filters.dateRange.end ? filters.dateRange.end.toISOString().split('T')[0] : ''}
                onChange={(e) => onFilterChange({
                  ...filters,
                  dateRange: { ...filters.dateRange, end: e.target.value ? new Date(e.target.value) : null }
                })}
              />
            </div>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <button
            onClick={() => toggleSection('status')}
            className="flex items-center justify-between w-full mb-3"
          >
            <span className="text-sm font-medium text-foreground">Status</span>
            <Icon
              name={expandedSections.includes('status') ? 'ChevronUp' : 'ChevronDown'}
              size={16}
              color="var(--color-muted-foreground)"
            />
          </button>
          {expandedSections.includes('status') && (
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  label={option.label}
                  checked={filters.status.includes(option.value)}
                  onChange={(e) => handleStatusChange(option.value, e.target.checked)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <button
            onClick={() => toggleSection('uf')}
            className="flex items-center justify-between w-full mb-3"
          >
            <span className="text-sm font-medium text-foreground">UF Origem</span>
            <Icon
              name={expandedSections.includes('uf') ? 'ChevronUp' : 'ChevronDown'}
              size={16}
              color="var(--color-muted-foreground)"
            />
          </button>
          {expandedSections.includes('uf') && (
            <div className="space-y-2">
              {ufOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  label={option.label}
                  checked={filters.ufOrigin.includes(option.value)}
                  onChange={(e) => handleUfOriginChange(option.value, e.target.checked)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <button
            onClick={() => toggleSection('ufDest')}
            className="flex items-center justify-between w-full mb-3"
          >
            <span className="text-sm font-medium text-foreground">UF Destino</span>
            <Icon
              name={expandedSections.includes('ufDest') ? 'ChevronUp' : 'ChevronDown'}
              size={16}
              color="var(--color-muted-foreground)"
            />
          </button>
          {expandedSections.includes('ufDest') && (
            <div className="space-y-2">
              {ufOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  label={option.label}
                  checked={filters.ufDestination.includes(option.value)}
                  onChange={(e) => handleUfDestinationChange(option.value, e.target.checked)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <button
            onClick={() => toggleSection('regime')}
            className="flex items-center justify-between w-full mb-3"
          >
            <span className="text-sm font-medium text-foreground">Regime Tributário</span>
            <Icon
              name={expandedSections.includes('regime') ? 'ChevronUp' : 'ChevronDown'}
              size={16}
              color="var(--color-muted-foreground)"
            />
          </button>
          {expandedSections.includes('regime') && (
            <div className="space-y-2">
              {taxRegimeOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  label={option.label}
                  checked={filters.taxRegime.includes(option.value)}
                  onChange={(e) => handleTaxRegimeChange(option.value, e.target.checked)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <button
            onClick={() => toggleSection('freshness')}
            className="flex items-center justify-between w-full mb-3"
          >
            <span className="text-sm font-medium text-foreground">Atualização de Dados</span>
            <Icon
              name={expandedSections.includes('freshness') ? 'ChevronUp' : 'ChevronDown'}
              size={16}
              color="var(--color-muted-foreground)"
            />
          </button>
          {expandedSections.includes('freshness') && (
            <div className="space-y-2">
              {freshnessOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  label={option.label}
                  checked={filters.apiDataFreshness.includes(option.value)}
                  onChange={(e) => handleFreshnessChange(option.value, e.target.checked)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <button
            onClick={() => toggleSection('amount')}
            className="flex items-center justify-between w-full mb-3"
          >
            <span className="text-sm font-medium text-foreground">Valor Total de Impostos</span>
            <Icon
              name={expandedSections.includes('amount') ? 'ChevronUp' : 'ChevronDown'}
              size={16}
              color="var(--color-muted-foreground)"
            />
          </button>
          {expandedSections.includes('amount') && (
            <div className="space-y-3">
              <Input
                label="Valor Mínimo (R$)"
                type="number"
                placeholder="0,00"
                value={filters.minTaxAmount}
                onChange={(e) => onFilterChange({ ...filters, minTaxAmount: e.target.value })}
              />
              <Input
                label="Valor Máximo (R$)"
                type="number"
                placeholder="0,00"
                value={filters.maxTaxAmount}
                onChange={(e) => onFilterChange({ ...filters, maxTaxAmount: e.target.value })}
              />
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default FilterSidebar;